// src/pages/HomeView/components/NewsFeed/NewsFeedSection.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { homeService } from '../../../../services/homeService';
import ConversationApi from '../../../../components/ConversationItem/api/ConversationApi';
import { authStorage } from '../../../../lib/storage';
import { config as envConfig } from '@/config/env';
import { ConversationItem } from '@/components/ConversationItem';
import type {
  ConversationDetail as ConversationDetailType,
  DashboardFilter,
} from '../../../../services/homeService';
import type { ApiConfig, AppConfig } from '@/components/ConversationItem/types';
import { SwapVert } from '@mui/icons-material';
import { conversationBus } from '@/lib/conversationBus';
import EditCard, { type EditCardRef } from '@/components/EditCard/EditCard';
import DuplicateCard, { type DuplicateCardRef } from '@/components/DuplicateCard/DuplicateCard';
import type { SideMenuItem } from '@/services/menuService';
import { apiService } from '@/services/api';
import { readViewCache, writeViewCache } from '@/lib/viewCache';

const NEWSFEED_CACHE_KEY = 'C2_Cache_Home_NewsFeed';

// Create wraps the saved card in `Conversation`; update returns it at the top level.
const getSavedConversation = (response: any): any =>
  response?.Conversation?.ConversationId ? response.Conversation : response;

/** Cards cached: enough for the first screen. */
const NEWSFEED_CACHE_LIMIT = 25;

// Backend responses can repeat a ConversationId (e.g. a conversation matching multiple joined layers/filters).
const dedupeByConversationId = (
  list: ConversationDetailType[],
): ConversationDetailType[] =>
{
  const seenIds = new Set<number>();
  return list.filter(c =>
  {
    if (seenIds.has(c.ConversationId)) return false;
    seenIds.add(c.ConversationId);
    return true;
  });
};

// Helper function to get dynamic API configuration from session
const getApiConfig = (): ApiConfig & {
  refreshToken: string;
  ExternalData: string;
  SocketServer: string;
  TenancyName: string;
  PersonId: string;
} =>
{
  const token = authStorage.getAccessToken() || '';
  const personId = authStorage.getPersonId() || '0';

  return {
    url: envConfig.api.baseUrl,
    token: token,
    refreshToken: '',
    ExternalData: '',
    SocketServer: envConfig.socket.server,
    TenancyName: envConfig.tenant.defaultName,
    PersonId: personId,
  };
};

// Helper function to get dynamic App configuration
const getAppConfig = (): AppConfig & {
  buildVersion?: string;
  webChaturl?: string;
  appPrefix?: string;
} =>
{
  const webUrl = import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/';
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost/Corgee.Web/';
  return {
    weburl: webUrl,
    apiurl: apiUrl,
    buildVersion: '1.0.0',
    webChaturl: webUrl + '/CHAT',
    appPrefix: '',
  };
};

// Storage Data Configuration - reads from real session/localStorage
const getStorageData = () =>
{
  let data: Record<string, any> = {};
  try
  {
    data = JSON.parse(sessionStorage.getItem('C2_data') || '{}');
  }
  catch
  {
    /* ignore */
  }

  let allLayers: any[] = [];
  try
  {
    allLayers = JSON.parse(sessionStorage.getItem('C2_AllLayers') || '[]');
  }
  catch
  {
    /* ignore */
  }

  let layersToHide: any[] = [];
  try
  {
    layersToHide = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
  }
  catch
  {
    /* ignore */
  }

  return {
    data,
    allLayers,
    layersToHide,
    layerId: parseInt(localStorage.getItem('C2_layerid') || '0'),
    conv_maxAllowedUpload: 20480000,
    enableChat: localStorage.getItem('C2_enableChat') === 'true',
    personid: parseInt(localStorage.getItem('C2_personid') || '0'),
  };
};

// Menu Configuration
const getConfigMenus = () => ({
  conversation: [
    'goto-project',
    'close-infowindow',
    'opendetails-newtab',
    'mark-redflag',
    'mark-important',
    'follow-conv',
    'create-itemtype',
    'convert-to',
    'duplicate-project',
    'copylink-to-clipboard',
    'move-to',
    'copy-to',
    'submit-time',
    'edit-history',
    'edit',
    'delete',
    'duplicate-item',
    'archive',
  ],
  reply: ['mark-important', 'mark-favorite', 'repost', 'edit', 'delete', 'create-itemtype'],
});

// Real Item Types Configuration
const realItemTypes = {
  Users: {
    Name: 'People',
    Icon: 'icons-menu-account-box',
  },
  Tasks: {
    Name: 'Tasks',
    Icon: 'icons-menu-epic',
  },
};

// User Data
const getUserData = () =>
{
  const givenName = localStorage.getItem('C2_givenname') || '';
  const imageProfile = localStorage.getItem('C2_imageprofile') || '';
  return {
    imageProfile: imageProfile || 'Images/user.png',
    personName: givenName || '',
  };
};

interface NewsFeedSectionProps {
  searchText: string;
  filters: DashboardFilter[];
  extraFilterParams?: Record<string, number>;
  /** Incremented by the parent to trigger a background refresh */
  refreshToken?: number;
  /** PersonId to view as (for viewing another user's home) */
  viewAsPersonId?: string;
}

import { useInfoWindow } from '../../../../contexts/InfoWindowContext';

// ... (existing imports)

export const NewsFeedSection: React.FC<NewsFeedSectionProps> = ({
  searchText,
  filters,
  extraFilterParams,
  refreshToken,
  viewAsPersonId,
}) =>
{
  // Use viewAsPersonId if provided, otherwise use current user's personId
  const personId = viewAsPersonId || authStorage.getPersonId() || '0';
  const socketConnectionId = useSocketConnectionId();
  const [conversations, setConversations] = useState<ConversationDetailType[]>(() =>
  {
    const cached = readViewCache<ConversationDetailType[]>(NEWSFEED_CACHE_KEY);
    return Array.isArray(cached) ? cached : [];
  });

  const [loading, setLoading] = useState(() =>
  {
    // If we have cached conversations, start with loading=false (so we don't show skeleton)
    // The background refresh will happen in useEffect but won't toggle loading to true if data exists.
    const cached = readViewCache<ConversationDetailType[]>(NEWSFEED_CACHE_KEY);
    return !(Array.isArray(cached) && cached.length > 0);
  });

  const [hasMore, setHasMore] = useState(true);
  // Separate flag for the infinite-scroll fetch, so the bottom indicator only
  // appears while paginating (not during a background refresh of page 1).
  const [loadingMore, setLoadingMore] = useState(false);
  const { openInfoWindow } = useInfoWindow();
  // Track if we should show "No More Items" message
  const [shouldShowNoMoreItems, setShouldShowNoMoreItems] = useState(false);
  const hasLoadedMoreRef = useRef(false);

  // Refs for scroll-based infinite scroll (avoid stale closures in listener)
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // The container only exists in the non-skeleton branch, so the scroll listener
  // has to wait for the node instead of being registered once on mount.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const setScrollContainer = useCallback((node: HTMLDivElement | null) =>
  {
    scrollContainerRef.current = node;
    setScrollEl(node);
  }, []);
  const pageNumberRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);
  const loadMoreRef = useRef<() => void>(() => {});
  hasMoreRef.current = hasMore;
  loadingRef.current = loading || loadingMore;

  // Always-current ref so layer handlers don't capture stale conversations
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  const handleAddLayerToCard = useCallback(
    async (
      convId: number,
      _e: React.MouseEvent | MouseEvent,
      layer: any,
      functions?: { onUpdateLayerToCard?: (layerId: number) => void },
    ) =>
    {
      const conv = conversationsRef.current.find(c => c.ConversationId === convId);
      if (!conv) return;

      const alreadyPresent = (conv.ConversationLayers || []).some(
        (cl: any) => cl.LayerId === layer.LayerId,
      );

      if (alreadyPresent)
      {
        functions?.onUpdateLayerToCard?.(layer.LayerId);
        return;
      }

      const newLayerName = (layer as any).Name || layer.LayerName || '';
      const newLayerColor = (layer as any).Color || layer.LayerColor || '';
      const updatedLayers = [
        ...(conv.ConversationLayers || []),
        {
          ConversationId: conv.ConversationId,
          LayerId: layer.LayerId,
          LayerName: newLayerName,
          Name: newLayerName,
          LayerColor: newLayerColor,
          Color: newLayerColor,
          isForAdd: true,
        },
      ];

      try
      {
        const response = await apiService.put<any[]>(
          'conversation/UpdateLayers',
          { ...conv, ConversationLayers: updatedLayers },
        );

        const rawLayers = Array.isArray(response) && response.length > 0 ? response : updatedLayers;
        const existingLayerMap = new Map((conv.ConversationLayers || []).map((l: any) => [l.LayerId, l]));
        const finalLayers = rawLayers.map((l: any) =>
        {
          if (l.Name || l.LayerName) return l;
          const src = existingLayerMap.get(l.LayerId);
          if (src)
          {
            return {
              ...l,
              Name: src.Name || src.LayerName || '',
              LayerName: src.LayerName || src.Name || '',
              LayerColor: l.LayerColor || src.LayerColor || src.Color || '',
              Color: l.Color || src.Color || src.LayerColor || '',
            };
          }
          if (l.LayerId === layer.LayerId)
          {
            return { ...l, Name: newLayerName, LayerName: newLayerName, LayerColor: newLayerColor, Color: newLayerColor };
          }
          return l;
        });

        setConversations(prev =>
          prev.map(c =>
            c.ConversationId === convId
              ? { ...c, ConversationLayers: finalLayers, isAddLayerToCard: true } as any
              : c,
          ),
        );
        functions?.onUpdateLayerToCard?.(layer.LayerId);
      }
      catch (err)
      {
        console.error('Failed to add layer to card:', err);
      }
    },
    [],
  );

  const handleAddLayerToProject = useCallback(
    async (
      convId: number,
      _e: React.MouseEvent | MouseEvent,
      layer: any,
      functions?: { onUpdateLayerToCard?: (layerId: number) => void; skipCardUpdate?: boolean },
    ) =>
    {
      const conv = conversationsRef.current.find(c => c.ConversationId === convId);
      if (!conv) return;

      const layerName = (layer as any).Name || layer.LayerName || '';
      const layerColor = (layer as any).Color || layer.LayerColor || '';
      let projectConversationId = (layer as any).ProjectConversationId;

      if (!projectConversationId && conv.ProjectId)
      {
        try
        {
          const proj = await apiService.get<any>(
            `Project/GetProject?projectId=${conv.ProjectId}`,
          );
          projectConversationId = proj?.ConversationId;
        }
        catch (_) { /* best-effort */ }
      }

      // Step 1: add layer to project conversation
      if (projectConversationId)
      {
        try
        {
          const projectConv = await apiService.post<any>(
            `conversation/GetConversationById?conversationId=${projectConversationId}`,
            null,
          );
          if (projectConv && projectConv.ConfigValueId !== 15)
          {
            let groups: any[] = [];
            try
            {
              const fetchedGroups = await apiService.get<any[]>(
                `group/GetGroupsByProject?projectId=${projectConv.ProjectId}`,
              );
              if (Array.isArray(fetchedGroups)) groups = fetchedGroups;
            }
            catch (_) { /* best-effort */ }

            await apiService.post<any>('conversation/updateconversation', {
              ...projectConv,
              CustomFields: [],
              ConversationGroups: groups.length > 0 ? groups : projectConv.ConversationGroups,
              ConversationLayers: [
                ...(projectConv.ConversationLayers || []),
                {
                  ConversationId: projectConv.ConversationId,
                  LayerId: layer.LayerId,
                  LayerName: layerName,
                  LayerColor: layerColor,
                  isForAdd: true,
                  IsActive: 1,
                },
              ],
              SocketConnectionId: socketConnectionId,
            });
          }
        }
        catch (err)
        {
          console.error('Failed to add layer to project conversation:', err);
        }
      }

      if (functions?.skipCardUpdate)
      {
        functions.onUpdateLayerToCard?.(layer.LayerId);
        return;
      }

      // Step 2: add layer to current conversation
      try
      {
        const alreadyPresent = (conv.ConversationLayers || []).some(
          (cl: any) => cl.LayerId === layer.LayerId,
        );

        if (!alreadyPresent)
        {
          const updatedLayers = [
            ...(conv.ConversationLayers || []),
            {
              ConversationId: conv.ConversationId,
              LayerId: layer.LayerId,
              LayerName: layerName,
              Name: layerName,
              LayerColor: layerColor,
              Color: layerColor,
              isForAdd: true,
            },
          ];

          const response = await apiService.put<any[]>(
            'conversation/UpdateLayers',
            { ...conv, ConversationLayers: updatedLayers, SocketConnectionId: socketConnectionId },
          );

          const rawLayers = Array.isArray(response) && response.length > 0 ? response : updatedLayers;
          const existingLayerMap = new Map((conv.ConversationLayers || []).map((l: any) => [l.LayerId, l]));
          const finalLayers = rawLayers.map((l: any) =>
          {
            if (l.Name || l.LayerName) return l;
            const src = existingLayerMap.get(l.LayerId);
            if (src)
            {
              return {
                ...l,
                Name: src.Name || src.LayerName || '',
                LayerName: src.LayerName || src.Name || '',
                LayerColor: l.LayerColor || src.LayerColor || src.Color || '',
                Color: l.Color || src.Color || src.LayerColor || '',
              };
            }
            if (l.LayerId === layer.LayerId)
            {
              return { ...l, Name: layerName, LayerName: layerName, LayerColor: layerColor, Color: layerColor };
            }
            return l;
          });

          setConversations(prev =>
            prev.map(c =>
              c.ConversationId === convId
                ? { ...c, ConversationLayers: finalLayers, isAddLayerToCard: true } as any
                : c,
            ),
          );
        }

        functions?.onUpdateLayerToCard?.(layer.LayerId);
      }
      catch (err)
      {
        console.error('Failed to add layer to current conversation:', err);
      }
    },
    [],
  );

  // Configuration state
  const [api] = useState(() => getApiConfig());
  const [config] = useState(() => getAppConfig());

  // EditCard refs and handlers
  const editCardRef = useRef<EditCardRef>(null);
  const editCardInitRef = useRef<((conv: any, itemType?: any) => void) | null>(null);

  const handleCreateConversation = useCallback((conv: any, itemType?: SideMenuItem) =>
  {
    editCardInitRef.current?.(conv, itemType);
    editCardRef.current?.openModal();
  }, []);

  const handleEditConversation = useCallback((_e: any, conv: any) =>
  {
    editCardInitRef.current?.(conv, null);
    editCardRef.current?.openModal();
  }, []);

  // DuplicateCard refs and handlers
  const duplicateCardRef = useRef<DuplicateCardRef>(null);
  const duplicateCardInitRef = useRef<((conv: any) => void) | null>(null);

  const handleDuplicateItem = useCallback((_e: any, conv: any) =>
  {
    duplicateCardInitRef.current?.(conv);
    duplicateCardRef.current?.openModal();
  }, []);

  useEffect(() =>
  {
    loadConversations();
  }, [searchText, filters]);

  // Reload when a socket refresh notification arrives from the parent
  useEffect(() =>
  {
    if (refreshToken && refreshToken > 0) loadConversations();
  }, [refreshToken]);

  // Recargar cuando cambien los layers ocultos
  useEffect(() =>
  {
    const handleLayersChanged = () =>
    {
      loadConversations();
    };
    window.addEventListener('layersToHideChanged', handleLayersChanged);
    return () => window.removeEventListener('layersToHideChanged', handleLayersChanged);
  }, [searchText, filters]);

  useEffect(() =>
  {
    const unsub = conversationBus.on('redflag-updated', ({ conversationId, isRedFlag }) =>
    {
      setConversations(prev =>
        prev.map(conv =>
          conv.ConversationId === conversationId ? { ...conv, IsRedFlag: isRedFlag } : conv,
        ),
      );
    });
    return unsub;
  }, []);

  // Replaces the card in place, or prepends it when it is not listed yet.
  const upsertConversation = useCallback((conv: any, insertWhenMissing = true) =>
  {
    const convId = conv?.ConversationId;
    if (!convId) return;

    setConversations(prev =>
    {
      const exists = prev.some(c => c.ConversationId === convId);
      if (exists) return prev.map(c => (c.ConversationId === convId ? conv : c));
      return insertWhenMissing ? [conv, ...prev] : prev;
    });
  }, []);

  // Pulls the full card from the API and lists it; `fallback` covers a failed request.
  const refreshConversationInList = useCallback(
    async (
      conversationId: number,
      options?: { fallback?: any; insertWhenMissing?: boolean },
    ): Promise<any> =>
    {
      const layersToHideValue = sessionStorage.getItem('C2_LayersToHide');
      const insertWhenMissing = options?.insertWhenMissing ?? true;

      try
      {
        const fullConversation = await ConversationApi.getConversationById(api, {
          conversationId,
          layersToHide:
            !layersToHideValue || layersToHideValue === 'null' ? '' : layersToHideValue,
          getAllReplies: true,
          isConversationDetailsView: false,
          totalReplies: 0,
        });

        upsertConversation(fullConversation, insertWhenMissing);
        return fullConversation;
      }
      catch
      {
        upsertConversation(options?.fallback, insertWhenMissing);
        return options?.fallback ?? null;
      }
    },
    [api, upsertConversation],
  );

  // Cards saved from another window reach the feed only through the bus.
  useEffect(() =>
  {
    const unsubCreated = conversationBus.on('conversation-created', ({ conversation }) =>
    {
      if (!conversation?.ConversationId) return;

      upsertConversation(conversation);
      void refreshConversationInList(conversation.ConversationId, { fallback: conversation });
    });

    const unsubUpdated = conversationBus.on('conversation-updated', ({ conversationId }) =>
    {
      if (conversationId) void refreshConversationInList(conversationId);
    });

    return () =>
    {
      unsubCreated();
      unsubUpdated();
    };
  }, [refreshConversationInList, upsertConversation]);

  const handleUpdateConversation = (convId: number, newAttrs: any) =>
  {
    setConversations(prev =>
      prev.map(conv => (conv.ConversationId === convId ? { ...conv, ...newAttrs } : conv)),
    );
  };

  // Build props for ConversationItem component
  const buildConversationItemProps = (conversation: ConversationDetailType, index: number) => ({
    index: index,
    conversation: { ...conversation, id: String(conversation.ConversationId) } as any,
    api: api,
    config: config,
    isViewCard: true, // Full card view for NewsFeed
    hasArchivedFilter: false,
    isConversationDetailsView: false,
    hideCustomFields: true,
    isHomeView: true,
    userData: getUserData(),
    storageData: getStorageData(),
    configMenus: getConfigMenus(),
    realItemTypes: realItemTypes,
    HasItemCreationPermission: localStorage.getItem('C2_hasItemCreationPermission') === 'true',
    ShowMoveto: true,
    corgeeFunctions: {
      loadingBarStart: () => {},
      loadingBarComplete: () => {},
      openInfoWindow: (conv: any) =>
      {
        // Callers pass either a plain id or the conversation object
        const id = typeof conv === 'number' ? conv : conv?.ConversationId;
        if (id) openInfoWindow(Number(id));
      },
      sendToBookmarks: (_conv: any) => {},
      moveTo: (_e: React.MouseEvent, _conv: any) => {},
      copyTo: (_e: React.MouseEvent, _conv: any) => {},
      createConversation: handleCreateConversation,
      editConversation: handleEditConversation,
      onDuplicateItem: handleDuplicateItem,
    },
    onUpdateConversation: (convId: number, newAttrs: any) =>
    {
      handleUpdateConversation(convId, newAttrs);
    },
    onAddReply: (_convId: number, _reply: any) => {},
    onUpdateReply: (convId: number, replyId: number, updates: any) =>
    {
      setConversations(prev =>
        prev.map(conv =>
        {
          if (conv.ConversationId !== convId) return conv;
          return {
            ...conv,
            Replies: (conv.Replies || []).map((r: any) =>
              r.ReplyId === replyId ? { ...r, ...updates } : r,
            ),
          };
        }),
      );
    },
    onDeleteReply: (_convId: number, _replyId: number, callback?: () => void) =>
    {
      if (callback) callback();
    },
    onAddLayerToCard: (e: any, layer: any, functions?: any) =>
      handleAddLayerToCard(conversation.ConversationId, e, layer, functions),
    onAddLayerToProject: (e: any, layer: any, functions?: any) =>
      handleAddLayerToProject(conversation.ConversationId, e, layer, functions),
  });

  const loadConversations = async () =>
  {
    try
    {
      // Only set loading true if we don't have conversations (or if we want to show a refresher)
      // For now, we keep the skeleton behavior only if we have no data
      setLoading(conversations.length > 0 ? false : true);

      // Use personId from component props (already defined in component scope)
      if (!personId || personId === '0')
      {
        console.warn('NewsFeedSection: No PersonId found');
        setLoading(false);
        return;
      }

      // Preparar filtros en formato string JSON
      const filtersString = JSON.stringify(
        filters.length > 0
          ? filters
          : [
            {
              Name: 'Me',
              Value: parseInt(personId),
              Text: 'ME',
              IsCustomField: false,
              FieldTypeId: null,
              CustomFields: null,
              ComparisonOperator: null,
              isChipFilter: false,
            },
          ],
      );

      const params = {
        CustomFields: [],
        DashboardId: 0,
        DashboardViewTypeId: 2,
        Filters: filtersString,
        Me: personId,
        PageNumber: 1,
        PageSize: 50,
        SearchText: searchText,
        IsHomeQuery: true,
        LayersToHide: (() =>
        {
          try
          {
            const arr = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
            return arr.length ? arr.join(',') : '';
          }
          catch
          {
            return '';
          }
        })(),
        ...extraFilterParams,
      };

      const response = await homeService.getHomeNewsFeed(params);

      // Extraer las conversaciones del primer grupo
      const allConversations = dedupeByConversationId(response[0]?.Conversations || []);

      setConversations(allConversations);
      setHasMore(allConversations.length >= 50);
      pageNumberRef.current = 1;

      // Save to cache
      writeViewCache(NEWSFEED_CACHE_KEY, allConversations.slice(0, NEWSFEED_CACHE_LIMIT));
    }
    catch (error)
    {
      console.error('NewsFeedSection: Error loading conversations:', error);
    }
    finally
    {
      setLoading(false);
    }
  };

  const loadMore = useCallback(async () =>
  {
    if (!hasMoreRef.current || loadingRef.current) return;

    // Set synchronously so a second scroll event in the same tick can't race past the guard
    loadingRef.current = true;
    setLoadingMore(true);
    const nextPage = pageNumberRef.current + 1;

    // Use personId from component scope
    const filtersString = JSON.stringify(
      filters.length > 0
        ? filters
        : [
          {
            Name: 'Me',
            Value: parseInt(personId || '0'),
            Text: 'ME',
            IsCustomField: false,
            FieldTypeId: null,
            CustomFields: null,
            ComparisonOperator: null,
            isChipFilter: false,
          },
        ],
    );

    try
    {
      const response = await homeService.getHomeNewsFeed({
        CustomFields: [],
        DashboardId: 0,
        DashboardViewTypeId: 2,
        Filters: filtersString,
        Me: personId || '0',
        PageNumber: nextPage,
        PageSize: 50,
        SearchText: searchText,
        IsHomeQuery: true,
        LayersToHide: (() =>
        {
          try
          {
            const arr = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
            return arr.length ? arr.join(',') : '';
          }
          catch
          {
            return '';
          }
        })(),
        ...extraFilterParams,
      });

      const moreConversations = dedupeByConversationId(response[0]?.Conversations || []);

      if (moreConversations.length > 0)
      {
        setConversations(prev =>
        {
          const existingIds = new Set(prev.map(c => c.ConversationId));
          const unique = moreConversations.filter(c => !existingIds.has(c.ConversationId));
          return [...prev, ...unique];
        });
        pageNumberRef.current = nextPage;
        setHasMore(moreConversations.length >= 50);
      }
      else
      {
        setHasMore(false);
      }
    }
    catch (error)
    {
      console.error('Error loading more conversations:', error);
    }
    finally
    {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, [filters, searchText, extraFilterParams, personId]);

  // Keep ref to latest loadMore for scroll handler (avoids re-registering listener)
  loadMoreRef.current = loadMore;

  // The indicator renders in flow after the last card, so it is born below the
  // viewport whenever the user is near the bottom (which is exactly when loadMore
  // fires). Scroll down once it is committed so it is actually seen; loadMore only
  // runs within 200px of the bottom, so this never moves the list far.
  useEffect(() =>
  {
    if (!loadingMore) return;
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [loadingMore]);

  // Scroll-to-bottom detection for infinite scroll
  useEffect(() =>
  {
    const el = scrollEl;
    if (!el) return;

    const handleScroll = () =>
    {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop - clientHeight < 200)
      {
        loadMoreRef.current();
        hasLoadedMoreRef.current = true;
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [scrollEl]);

  // Check if we should show "No More Items" message
  // Show it if: !hasMore AND (user has scrolled/loaded more OR content doesn't fill the viewport)
  useEffect(() =>
  {
    if (!hasMore && conversations.length > 0)
    {
      const el = scrollContainerRef.current;
      if (!el) return;
      const { scrollHeight, clientHeight } = el;
      // Show if user has loaded more, or if content doesn't fill viewport (no scrollbar needed)
      setShouldShowNoMoreItems(hasLoadedMoreRef.current || scrollHeight <= clientHeight);
    }
    else
    {
      setShouldShowNoMoreItems(false);
    }
  }, [hasMore, conversations.length, loading, loadingMore]);

  if (loading && conversations.length === 0)
  {
    return (
      <div className="newsfeed-section flex flex-col h-full">
        {/* Dashboard Group Header - Fixed */}
        <div className="border-gray-300 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <SwapVert htmlColor="#62aacb" />
            <h3 className="text-sm font-bold text-gray-500">NewsFeed</h3>
          </div>
        </div>

        {/* Scrollable Content - Skeleton */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 p-3 border-t border-gray-300">
          {/* Skeleton loaders - Card style */}
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse bg-white rounded shadow-sm p-4">
              {/* Card Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                </div>
              </div>
              {/* Card Content */}
              <div className="space-y-2 mb-3">
                <div className="h-3 bg-gray-200 rounded w-full"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                <div className="h-3 bg-gray-100 rounded w-4/6"></div>
              </div>
              {/* Card Footer - Layers */}
              <div className="flex gap-2">
                <div className="h-5 bg-gray-200 rounded w-16"></div>
                <div className="h-5 bg-gray-200 rounded w-12"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="newsfeed-section flex flex-col h-full relative">
      {/* Header - Fixed */}
      <div className=" border-gray-300 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <SwapVert htmlColor="#62aacb" />
          <h3 className="text-sm font-bold text-gray-500">NewsFeed</h3>
        </div>
      </div>

      {/* Skeleton Overlay - shows while content renders behind */}

      {/* Scrollable Content - renders behind skeleton */}
      <div ref={setScrollContainer} className="flex-1 overflow-y-auto p-3 border-t border-gray-300">
        {conversations.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">No news feed items to display</p>
          </div>
        ) : (
          <div className="space-y-3">
            {conversations.map((conv, index) => (
              <div key={conv.ConversationId} className="overflow-hidden">
                <ConversationItem {...buildConversationItemProps(conv, index)} />
              </div>
            ))}

            {/* No More Items message */}
            {shouldShowNoMoreItems && !loading && !loadingMore && (
              <div className="newsfeed-no-more-items">No More Items</div>
            )}
          </div>
        )}

        {/* Infinite scroll loading indicator */}
        {loadingMore && (
          <div className="flex justify-center py-3">
            <div
              className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: '#62aacb', borderTopColor: 'transparent' }}
            />
          </div>
        )}
      </div>

      {/* DuplicateCard modal */}
      <DuplicateCard
        ref={duplicateCardRef}
        config={{
          apiUrl: api.url,
          authToken: api.token,
          SocketConnectionId: socketConnectionId,
          DashboardId: 0,
          attachReactFunctions: (fns: any) =>
          {
            duplicateCardInitRef.current = fns.initConfig;
          },
          functions: {
            loadingBarStart: () => {},
            loadingBarComplete: () => {},
            conversationResponse: async (_requestConfig: any, response: any) =>
            {
              const saved = getSavedConversation(response);
              if (saved?.ConversationId)
              {
                await refreshConversationInList(saved.ConversationId, { fallback: saved });
              }
            },
          },
          params: { Conversation: {} },
        }}
      />

      {/* EditCard modal for Create/Edit functionality */}
      <EditCard
        ref={editCardRef}
        config={{
          apiUrl: api.url,
          authToken: api.token,
          SocketConnectionId: socketConnectionId,
          attachReactFunctions: (fns: any) =>
          {
            editCardInitRef.current = fns.initConfig;
          },
          functions: {
            loadingBarStart: () => {},
            loadingBarComplete: () => {},
            conversationResponse: async (_requestConfig: any, response: any) =>
            {
              const saved = getSavedConversation(response);
              if (!saved?.ConversationId) return;

              const fullConversation = await refreshConversationInList(saved.ConversationId, {
                fallback: saved,
              });

              // Reload the parent so it shows its new child; never list it on its own
              const parentId = saved.ParentConversationId || saved.RelatedConversationId;
              if (!parentId) return;

              const parentConversation = await refreshConversationInList(parentId, {
                insertWhenMissing: false,
              });

              if (parentConversation)
              {
                conversationBus.emit('reply-added', {
                  conversationId: parentId,
                  reply: fullConversation,
                });
              }
            },
          },
          params: {
            Conversation: {},
          },
        }}
      />
    </div>
  );
};
